import Axios from "../utils/axiosInterceptor";
import { API_URL, USER_MANAGEMENT } from "../utils/constants";
import { errorMessage } from "../utils/notificationUtils";

export const getAllRoles = async (): Promise<any> => {
  try {
    const resp = await Axios.get(USER_MANAGEMENT.ALL_ROLES);
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN GETTING ALL ROLES";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAllHospitals = async (): Promise<any> => {
  try {
    const resp = await Axios.get(USER_MANAGEMENT.ALL_HOSPITALS);
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN GETTING ALL HOSPITALS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const createUser = async (userData: any): Promise<any> => {
  try {
    const resp = await Axios.post(USER_MANAGEMENT.CREATE_USER, userData);
    return resp?.data;
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || "ERROR IN CREATING USER";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const updateUser = async (userId: any, userData: any): Promise<any> => {
  try {
    const resp = await Axios.put(
      `${USER_MANAGEMENT.UPDATE_USER}/${userId}`,
      userData,
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || "ERROR IN UPDATING USER";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAllActiveUsers = async (searchTerm: any): Promise<any> => {
  try {
    const resp = await Axios.get(USER_MANAGEMENT.ALL_ACTIVE_USERS, {
      headers: {
        searchTerm: searchTerm || "",
      },
    });
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN GETTING ALL ACTIVE USERS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const assignHealthCare = async (
  healthCareIds: any,
  request: any,
): Promise<any> => {
  try {
    const payload = {
      healthCareIds: healthCareIds,
      request: request,
    };
    const resp = await Axios.post(USER_MANAGEMENT.ASSIGN_HEALTHCARE, payload);
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN ASSIGNING HEALTHCARE";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getUserAssignedHospitals = async (userId: any): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${USER_MANAGEMENT.USER_ASSIGNED_HOSPITALS}/${userId}`,
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER ASSIGNED HOSPITALS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const deleteUser = async (userId: any): Promise<any> => {
  try {
    const resp = await Axios.delete(`${USER_MANAGEMENT.DELETE_USER}/${userId}`);
    return resp?.data;
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || "ERROR IN DELETING USER";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const deleteAssignedHospital = async (
  hospitalId: any,
  userId: any,
): Promise<any> => {
  try {
    const resp = await Axios.delete(
      `${USER_MANAGEMENT.DELETE_ASSIGNED_HOSPITAL}/${hospitalId}/health_care_management_user/${userId}`,
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN DELETING ASSIGNED HOSPITAL";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const resetUserPassword = async (resetPayload: any): Promise<any> => {
  try {
    const resp = await Axios.post(
      USER_MANAGEMENT.RESET_USER_PASSWORD,
      resetPayload,
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN RESETTING USER PASSWORD";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUser = async (
  healthCareId: any,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
) => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users`,
      {
        params: {
          startDate,
          endDate,
          schemeIds: selectedSchemeIds.join(","),
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN GETTING ASSIGNED USER";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUserStageCounts = async (
  healthCareId: any,
  userId: any,
  visitType: string,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users/${userId}/task-counts-by-visit-stage`,
      {
        params: {
          visitType,
          startDate,
          endDate,
          schemeIds: selectedSchemeIds.join(","),
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER TASK STAGE COUNTS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUserStageTasks = async (
  healthCareId: any,
  userId: any,
  visitType: string,
  visitStage: string,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users/${userId}/tasks`,
      {
        params: {
          visitType,
          visitStageName: visitStage,
          startDate,
          endDate,
          schemeIds: selectedSchemeIds.join(","),
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER TASK STAGE TASKS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUserManagement = async (
  healthCareId: any,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
  roleIds?: number[],
) => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users/claim-workflow`,
      {
        params: {
          startDate,
          endDate,
          schemeIds: Array.isArray(selectedSchemeIds)
            ? selectedSchemeIds.join(",")
            : "",
          ...(Array.isArray(roleIds) && roleIds.length > 0
            ? { roleIds: roleIds.join(",") }
            : {}),
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN GETTING ASSIGNED USER";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUserManagementStageCounts = async (
  healthCareId: any,
  userId: any,
  visitType: string,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
  workflowStage?: string,
  visitStageId?: any,
  stateIds?: any,
): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users/${userId}/claim-workflow/task-counts`,
      {
        params: {
          visitType,
          startDate,
          endDate,
          schemeIds: selectedSchemeIds.join(","),
          workflowStage,
          visitStageId,
          stateIds,
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER TASK STAGE COUNTS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const getAssignedUserManagementStageCountsTabs = async (
  healthCareId: any,
  userId: any,
  visitType: string,
  visitStage: string,
  startDate: string,
  endDate: string,
  selectedSchemeIds: any,
  workflowStage?: string,
  visitStageId?: any,
  stateIds?: any,
): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/userManagement/stats/healthCare/${healthCareId}/users/${userId}/claim-workflow/tasks`,
      {
        params: {
          visitType,
          visitStageName: visitStage,
          startDate,
          endDate,
          schemeIds: selectedSchemeIds.join(","),
          workflowStage,
          visitStageId,
          stateIds,
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER TASK STAGE TASKS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const pendingDocuments = async (payload: FormData) => {
  try {
    const res = await Axios.post(
      `${API_URL}/api/v1/doc-upload/orchestrate-pending-documents`,
      payload,
    );
    return res.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message || "ERROR IN UPLOADING PENDING DOCUMENTS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};

export const pendingDocumentHistory = async (
page?: number,
size?: number,
): Promise<any> => {
  try {
    const resp = await Axios.get(
      `${API_URL}/api/v1/doc-upload/orchestrator`,
      {
        params: {
          page,
          size,
        },
      },
    );
    return resp?.data;
  } catch (error: any) {
    const errorMsg =
      error?.response?.data?.message ||
      "ERROR IN GETTING USER TASK STAGE TASKS";
    errorMessage(errorMsg);
    throw new Error(errorMsg);
  }
};
